import { useState, useMemo } from 'react';
import { Header } from '@/components/Header';
import { UserListRow } from '@/components/UserListRow';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { GuestDetailsDrawer } from '@/components/guests/GuestDetailsDrawer';
import { ExportGuestLogDialog } from '@/components/guests/ExportGuestLogDialog';
import { useActionBar } from '@/lib/context/ActionBarContext';
import { useGuestLogInfinite } from '@/lib/hooks/useGuests';
import { formatDateTimeForList, isDateInFuture } from '@/lib/utils/date.utils';
import { getInitials } from '@/lib/utils/name.utils';
import { GuestLogContent } from '@/content/GuestLogContent';
import type { Guest } from '@/lib/types/database';

/**
 * Date range type for filtering guests
 */
type DateRange = {
  dateFrom?: string;
  dateTo?: string;
};

/**
 * Get date range for last N days including today
 * Uses ISO datetime strings to include full day ranges
 */
function getDateRangeForDays(days: number): DateRange {
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const dateFrom = new Date(now);
  dateFrom.setDate(dateFrom.getDate() - days);
  dateFrom.setHours(0, 0, 0, 0);

  return {
    dateFrom: dateFrom.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get date range for last N weeks
 */
function getDateRangeForWeeks(weeks: number): DateRange {
  return getDateRangeForDays(weeks * 7);
}

/**
 * Get date range for last N months including today
 * Uses ISO datetime strings to include full day ranges
 */
function getDateRangeForMonths(months: number): DateRange {
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const dateFrom = new Date(now);
  dateFrom.setMonth(dateFrom.getMonth() - months);
  dateFrom.setHours(0, 0, 0, 0);

  return {
    dateFrom: dateFrom.toISOString(),
    dateTo: endOfToday.toISOString(),
  };
}

/**
 * Get "All Time" date range (no filtering)
 */
function getAllTimeDateRange(): DateRange {
  return { dateFrom: undefined, dateTo: undefined };
}

/**
 * Get the date portion (YYYY-MM-DD) from an ISO datetime string
 */
function getDatePortion(isoString?: string): string {
  return isoString?.split('T')[0] ?? '';
}

/**
 * Check if the current date range represents "All Time"
 */
function isAllTime(range: DateRange): boolean {
  return range.dateFrom === undefined && range.dateTo === undefined;
}

export function GuestLog() {
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    getAllTimeDateRange()
  );

  // Fetch guests with infinite scroll pagination
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGuestLogInfinite({
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    });

  // Flatten all pages into a single array
  const allGuests = data?.pages.flat() ?? [];

  // Handle guest click
  const handleGuestClick = (guest: Guest) => {
    setSelectedGuest(guest);
    setIsDrawerOpen(true);
  };

  // Handle load more
  const handleLoadMore = () => {
    fetchNextPage();
  };

  // Handle export dialog open
  const handleExportClick = () => {
    setIsExportDialogOpen(true);
  };

  // Set up ActionBar
  useActionBar({
    visible: true,
    actions: [
      {
        key: 'export',
        label: GuestLogContent.export.label,
        variant: 'secondary',
        fullWidth: true,
        onClick: handleExportClick,
      },
    ],
  });

  // Transform guests to list items
  const guestListItems = useMemo(() => {
    return allGuests.map((guest) => {
      const guestName = `${guest.first_name} ${guest.last_name}`.trim();
      const timestamp = formatDateTimeForList(
        guest.visit_date,
        guest.created_at
      );
      const isUpcoming = isDateInFuture(guest.visit_date);

      return {
        guest,
        name: guestName,
        initials: getInitials(guestName),
        timestamp,
        badge: isUpcoming ? (
          <Pill variant="secondary">Upcoming</Pill>
        ) : undefined,
      };
    });
  }, [allGuests]);

  const showLoadMore = !isLoading && hasNextPage && !isFetchingNextPage;

  return (
    <div className="flex flex-col">
      <Header enableBack />
      <div className="w-full max-w-4xl mx-auto px-4 pb-8 bg-slate-700">
        <h1 className="text-slate-50 mb-6">{GuestLogContent.pageTitle}</h1>

        {/* Date Filter - Simple implementation for now */}
        <div className="mb-6">
          <label className="text-body text-slate-50 mb-2 block">
            {GuestLogContent.dateFilter.label}
          </label>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={isAllTime(dateRange) ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(getAllTimeDateRange())}
            >
              {GuestLogContent.dateFilter.allTime}
            </Button>
            <Button
              variant={
                getDatePortion(dateRange.dateFrom) ===
                getDatePortion(getDateRangeForDays(1).dateFrom)
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              onClick={() => setDateRange(getDateRangeForDays(1))}
            >
              {GuestLogContent.dateFilter.lastDay}
            </Button>
            <Button
              variant={
                getDatePortion(dateRange.dateFrom) ===
                getDatePortion(getDateRangeForWeeks(1).dateFrom)
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              onClick={() => setDateRange(getDateRangeForWeeks(1))}
            >
              {GuestLogContent.dateFilter.lastWeek}
            </Button>
            <Button
              variant={
                getDatePortion(dateRange.dateFrom) ===
                getDatePortion(getDateRangeForMonths(1).dateFrom)
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              onClick={() => setDateRange(getDateRangeForMonths(1))}
            >
              {GuestLogContent.dateFilter.lastMonth}
            </Button>
            <Button
              variant={
                getDatePortion(dateRange.dateFrom) ===
                getDatePortion(getDateRangeForMonths(6).dateFrom)
                  ? 'default'
                  : 'outline'
              }
              size="sm"
              onClick={() => setDateRange(getDateRangeForMonths(6))}
            >
              {GuestLogContent.dateFilter.lastSixMonths}
            </Button>
          </div>

          <p className="text-small text-slate-500 mt-2">
            Note that fellows can schedule visits in advance
          </p>
        </div>

        {/* Guest List */}
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : guestListItems.length === 0 ? (
          <div className="rounded-2xl border border-slate-500 px-4 py-8 text-center text-body text-slate-300">
            {GuestLogContent.emptyStates.noGuestsInRange}
          </div>
        ) : (
          <>
            <ul className="bg-slate-600 rounded-xl mb-4">
              {guestListItems.map((item) => (
                <UserListRow
                  key={item.guest.id}
                  name={item.name}
                  initials={item.initials}
                  timestamp={item.timestamp}
                  onClick={() => handleGuestClick(item.guest)}
                  showChevron
                  badge={item.badge}
                />
              ))}
            </ul>

            {/* Load More Button */}
            {showLoadMore && (
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
                className="w-full h-16"
              >
                {isFetchingNextPage
                  ? 'Loading...'
                  : GuestLogContent.pagination.loadMore}
              </Button>
            )}

            {/* Loading More Skeletons */}
            {isFetchingNextPage && (
              <div className="space-y-2 mt-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-xl" />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Guest Details Drawer */}
      <GuestDetailsDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        guest={selectedGuest}
      />

      {/* Export Dialog */}
      <ExportGuestLogDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
      />
    </div>
  );
}
